import { Component, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ChangeDetectionStrategy, EventEmitter, Output } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatIconButton } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TitleService } from '../../services/title.service';
import { CommonModule } from '@angular/common';
import { UsermenuComponent } from '../usermenu/usermenu.component';
import { AppVersionService } from '../../services/appVersion.service';
import { AlgorithmVersionService } from '../../services/algorithmVersion.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-toolbar',
  imports: [MatIconModule, MatToolbarModule, MatIconButton, CommonModule, UsermenuComponent, MatTooltipModule],
  templateUrl: './toolbar.component.html',
  styleUrl: './toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('titleAnimation', [
      transition(':enter', [style({ opacity: 0, transform: 'translateY(-40px)' }), animate('225ms ease-out')]),
      transition(':leave', [animate('195ms 0.1s ease-in', style({ opacity: 0, transform: 'translateY(-40px)' }))])
    ]),
    trigger('subtitleAnimation', [
      transition(':enter', [style({ opacity: 0, transform: 'translateY(40px)' }), animate('225ms ease-out')]),
      transition(':leave', [animate('195ms 0.1s ease-in', style({ opacity: 0, transform: 'translateY(40px)' }))])
    ])
  ],
  providers: [] // Provide the service here
})
export class ToolbarComponent {
  @Output() toggleMenu = new EventEmitter();

  envstring = environment.environmentString;
  isProduction = environment.production;
  appVersion = signal<string | undefined>(undefined);
  appVersionLastUpdated = signal<Date | undefined>(undefined);
  algorithmVersion = signal<string | undefined>(undefined);
  algorithmVersionLastUpdated = signal<Date | undefined>(undefined);

  constructor(
    public readonly title: TitleService,
    private readonly appVersionService: AppVersionService,
    private readonly algorithmVersionService: AlgorithmVersionService
  ) {}

  ngOnInit(): void {
    this.getAppVersion();
    this.getAlgorithmVersion();
  }

  private getAppVersion(): void {
    this.appVersionService.getVersion().subscribe({
      next: (versionData) => {
        this.appVersion.set(versionData.version);
        this.appVersionLastUpdated.set(new Date(versionData.lastUpdated));
      },
      error: (error) => {
        console.log('Error fetching app version:', error);
        this.appVersion.set('N/A');
      }
    });
  }

  private getAlgorithmVersion(): void {
    this.algorithmVersionService.getVersion().subscribe({
      next: (versionData) => {
        this.algorithmVersion.set(versionData.version);
        this.algorithmVersionLastUpdated.set(new Date(versionData.lastUpdated));
      },
      error: (error) => {
        console.error('Error fetching algorithm version:', error);
        this.algorithmVersion.set('N/A');
      }
    });
  }

  getTooltipText(): string {
    const appVersionText = this.appVersion()
      ? `App version: ${this.appVersion()} (${this.formatDate(this.appVersionLastUpdated())})`
      : '';
    const algorithmVersionText = this.algorithmVersion()
      ? `Algorithm version: ${this.algorithmVersion()} (${this.formatDate(this.algorithmVersionLastUpdated())})`
      : '';
    const envText = `Environment: ${this.envstring}`;

    return [appVersionText, algorithmVersionText, envText]
      .filter(text => text)
      .join('\n');
  }

  private formatDate(date: Date | undefined): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  }
}
